import {
  EcommerceAPIApi as EcommerceApi,
  type UIGarage,
  type TicketFieldRequirementsConfig,
} from '@parkingbase/ecommerce-api-test';

import type { RawAxiosRequestConfig } from 'axios';
import { BaseApiService } from './baseApi.service';

/**
 * Fetches the lightweight UIGarage list (per-garage gateway facts) from the
 * `/garages/forEcommerce` endpoint. The backend resolves the company from the
 * request domain — no companyId param needed.
 *
 * Auth-gated: returns 401 for unauthenticated callers, so only call it for an
 * authenticated session. Used by payment surfaces for per-garage gateway
 * resolution (BOEC-10276).
 */
export class GarageService extends BaseApiService {
  protected garageApi: EcommerceApi;

  constructor() {
    super();
    this.garageApi = new EcommerceApi(this.apiConfig, undefined, this.apiClient);
  }

  async getUIGarages(): Promise<UIGarage[]> {
    const response = await this.garageApi.getGaragesForEcommerce();
    return response.data ?? [];
  }

  /**
   * Resolves a garage's per-context ticket field requirements (PB-2555) via
   * `GET /garage/{garageId}/ticketFieldRequirements/{company}` (auth-gated).
   *
   * Calls the raw axios client instead of `GarageApi.getTicketFieldRequirements` — that
   * generated method isn't published yet; this ships without blocking on an api-dev
   * republish (same pattern as `GuestPassService.resendGuestPassCoupon`).
   */
  async getTicketFieldRequirements(
    garageId: string,
    company: string,
    options?: RawAxiosRequestConfig
  ): Promise<TicketFieldRequirementsConfig> {
    const response = await this.garageApi.getTicketFieldRequirements(garageId, company, options)
    return response.data;
  }
}
